import _range from 'lodash/range';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _min from 'lodash/min';
import _last from 'lodash/last';
import _keyBy from 'lodash/keyBy';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import { ofType, combineEpics } from 'redux-observable';
import { EMPTY, timer, of, from, concat, forkJoin } from 'rxjs';
import {
  delay,
  mergeMap,
  switchMap,
  takeUntil,
  toArray,
  catchError,
  concatMap,
} from 'rxjs/operators';
import { ajax } from 'rxjs/ajax';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';

import { GE_ORDER_STATE, GE_WEB_ORDER_STATE } from 'constants/order-constants';
import fetchUtil from 'common/utils/fetch';
import fetchObservableUtil from 'common/utils/fetch-observable';
import {
  UPDATE_ONGOING_ORDER_STATE_CHANGES,
  UPDATE_ONGOING_ORDER_COUNTING,
  FETCH_ONGOING_DELIVERIES_INTERVAL,
  PAX_FETCH_ONGOING_DELIVERIES_INTERVAL,
  FETCH_ONGOING_DELIVERIES,
  PAX_FETCH_ONGOING_DELIVERIES,
  FETCH_ALL_PREVIOUS_BOOKINGS,
  STOP_FETCH_ONGOING_DELIVERIES_INTERVAL,
  STOP_PAX_FETCH_ONGOING_DELIVERIES_INTERVAL,
  FETCH_HISTORY_CONSECUTIVELY_V1,
  SUBMIT_CANCEL_REASONS_SINGLE_ORDER,
  SUBMIT_CANCEL_REASONS_FOR_MULTIPLE_ORDERS,
  fetchOngoingDeliveriesAction,
  fetchAllPreviousBookingsSuccessAction,
  paxFetchOngoingDeliveriesAction,
  stopFetchOngoingDeliveriesIntervalAction,
  fetchCompletedDeliveriesAction,
  stopPaxFetchOngoingDeliveriesIntervalAction,
  paxFetchCompletedDeliveriesAction,
  paxFetchOngoingDeliveriesIntervalAction,
  fetchOngoingDeliveriesIntervalAction,
} from 'actions/history-actions';
import { isPaxSelector, userCompanyIDSelector } from 'selectors/app-selector';
import { buildHeaders } from 'actions/fetch-helper';
import endpoints from 'actions/history-endpoints';
import { enableCancellationRules } from 'helper/configuration';
import { buildAjaxCredentials } from '../../helper/ajax-helper';
import { buildCredentials } from '../../helper/session-helper';

const ONGOING_DELIVERIES_INTERVAL = 30000;
const MAX_PAGE_SIZE = 30;

// 24/2/2021 Limiting max rows in download as we experienced issue where unlimited rows cause large QPS spikes.
const MAX_BULK_DOWNLOAD_SIZE = 300;

// 09/3/2021 Not using this until BE implements proper pagination
const fetchHistoryConsecutivelyV1Epic = action$ =>
  action$.pipe(
    ofType(FETCH_HISTORY_CONSECUTIVELY_V1),
    mergeMap(({ payload: { params, callback } }) => {
      const { start, companyID, ...otherParams } = params;
      const totalToFetch = _min([otherParams.limit, MAX_BULK_DOWNLOAD_SIZE]);
      const pageNumbers = _range(start, totalToFetch, MAX_PAGE_SIZE);

      from(pageNumbers)
        .pipe(
          concatMap(page =>
            ajax({
              url: endpoints.getHistoryEpt({ start: page, ...otherParams }),
              method: 'GET',
              headers: buildHeaders(null, null, companyID, true),
              withCredentials: buildAjaxCredentials(),
            }),
          ),
          mergeMap(result => from(result.response.deliveries)),
          toArray(),
          catchError(e => []),
        )
        .subscribe(result => {
          callback({ deliveries: result });
        });

      return EMPTY;
    }),
  );

const fetchOngoingDeliveriesIntervalEpic = action$ =>
  action$.pipe(
    ofType(FETCH_ONGOING_DELIVERIES_INTERVAL),
    switchMap(action => {
      const { companyID, keywords } = action.payload;
      return timer(0, ONGOING_DELIVERIES_INTERVAL).pipe(
        takeUntil(action$.pipe(ofType(STOP_FETCH_ONGOING_DELIVERIES_INTERVAL))),
        switchMap(() => {
          if (keywords) {
            trackClick(event.BOOKINGS_CLICKS, cat.GE_BOOKINGS, 'SEARCH', {
              [custom.COMPANY_ID]: companyID,
              TEXT: keywords,
            });
          }
          return of(fetchOngoingDeliveriesAction({ companyID, keywords }));
        }),
      );
    }),
  );

const fetchPaxOngoingDeliveriesIntervalEpic = action$ =>
  action$.pipe(
    ofType(PAX_FETCH_ONGOING_DELIVERIES_INTERVAL),
    switchMap(action =>
      timer(0, ONGOING_DELIVERIES_INTERVAL).pipe(
        takeUntil(
          action$.pipe(ofType(STOP_PAX_FETCH_ONGOING_DELIVERIES_INTERVAL)),
        ),
        switchMap(() => of(paxFetchOngoingDeliveriesAction())),
      ),
    ),
  );
const getLastOrderEventTimestamp = order => {
  if (!order || _isEmpty(order.orderEvents)) return null;

  const lastOrderEventKey = _last(Object.keys(order.orderEvents));
  return order.orderEvents[lastOrderEventKey].Timestamp;
};

const recentUpdateSectionEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      FETCH_ONGOING_DELIVERIES.SUCCESS,
      PAX_FETCH_ONGOING_DELIVERIES.SUCCESS,
    ),
    switchMap(action => {
      const state = state$.value;
      const ongoingOrders = _get(state, 'history.ongoingOrders', []).sort(
        (order1, order2) => {
          const order1LastEventTs = getLastOrderEventTimestamp(order1);
          const order1CreatedTs = _get(order1, 'createMeta.createAtInTs');
          const order2LastEventTs = getLastOrderEventTimestamp(order2);
          const order2CreatedTs = _get(order2, 'createMeta.createAtInTs');

          return (
            (!order2LastEventTs ? order2CreatedTs : order2LastEventTs) -
            (!order1LastEventTs ? order1CreatedTs : order1LastEventTs)
          );
        },
      );

      const prevOngoingOrders = _get(state, 'history.prevOngoingOrders', []);
      const ongoingOrderMap = _keyBy(ongoingOrders, order => order.deliveryID);
      const someBookingEnded = prevOngoingOrders.some(
        prevBooking =>
          !_isEmpty(prevBooking.deliveryID) &&
          _isEmpty(ongoingOrderMap[prevBooking.deliveryID]),
      );
      const queryString = _get(state, 'history.completedOrderQueryParams');

      return concat(
        of({
          type: UPDATE_ONGOING_ORDER_STATE_CHANGES,
          payload: ongoingOrders,
        }),
        _isEmpty(ongoingOrders)
          ? of(
              isPaxUser()
                ? stopPaxFetchOngoingDeliveriesIntervalAction()
                : stopFetchOngoingDeliveriesIntervalAction(),
            )
          : EMPTY,
        someBookingEnded
          ? of(
              isPaxUser()
                ? paxFetchCompletedDeliveriesAction({
                    queryString,
                  })
                : fetchCompletedDeliveriesAction({
                    queryString,
                  }),
            )
          : EMPTY,
      );
    }),
  );

const ongoingOrderCountingEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      FETCH_ONGOING_DELIVERIES.SUCCESS,
      PAX_FETCH_ONGOING_DELIVERIES.SUCCESS,
    ),
    switchMap(action => {
      const state = state$.value;
      const ongoingOrders = _get(state, 'history.ongoingOrders', []);
      const countingOngoingOrders = ongoingOrders.reduce(
        (acc, order) => {
          const { status } = order;
          if (status === GE_WEB_ORDER_STATE.Allocating) {
            return {
              ...acc,
              allocating: acc.allocating + 1,
            };
          }

          if (
            status === GE_ORDER_STATE.PickingUp ||
            status === GE_ORDER_STATE.PendingPickUp
          ) {
            return {
              ...acc,
              pickingUp: acc.pickingUp + 1,
            };
          }

          if (
            status === GE_ORDER_STATE.DroppingOff ||
            status === GE_ORDER_STATE.PendingDropOff
          ) {
            return {
              ...acc,
              droppingOff: acc.droppingOff + 1,
            };
          }

          return {
            ...acc,
            processing: acc.processing + 1,
          };
        },
        {
          total: ongoingOrders.length,
          processing: 0, // The rest of active orders
          allocating: 0, // Allocating
          pickingUp: 0, // PickingUp, PendingPickUp
          droppingOff: 0, // DroppingOff, PendingDropOff
        },
      );

      return of({
        type: UPDATE_ONGOING_ORDER_COUNTING,
        payload: countingOngoingOrders,
      });
    }),
  );

const fetchBookings = async (
  urlSearchParams,
  offset,
  companyID,
  totalCount = 1e6,
  bookings = [],
) => {
  if (bookings.length >= totalCount) {
    return bookings;
  }
  urlSearchParams.set('params.offset', offset);
  let historyDeliveries;
  if (isPaxUser()) {
    historyDeliveries = await fetchUtil(
      endpoints.getPaxHistoryDeliveriesEpt(urlSearchParams.toString()),
      {
        method: 'GET',
        headers: buildHeaders(null, null, null, true),
        credentials: buildCredentials(),
      },
    );
  } else {
    historyDeliveries = await fetchUtil(
      endpoints.getHistoryDeliveriesEpt(urlSearchParams.toString()),
      {
        method: 'GET',
        headers: buildHeaders(null, null, companyID, true),
        credentials: buildCredentials(),
      },
    );
  }
  const { result } = historyDeliveries;
  const error = _get(result, 'deliveries[0].errors');
  if (error || !result || _isEmpty(result)) {
    return bookings;
  }

  const currentBookings = bookings.concat(result.deliveries);
  return fetchBookings(
    urlSearchParams,
    offset + MAX_PAGE_SIZE,
    companyID,
    result.totalCount,
    currentBookings,
  );
};

// 2021/03/10 - Not being used until BE implements pagination correctly
const fetchAllPreviousBookingsEpic = (action$, state$) =>
  // consecutively fetch all the previous bookings
  action$.pipe(
    ofType(FETCH_ALL_PREVIOUS_BOOKINGS),
    mergeMap(async action => {
      const { 'params.states': states, companyID, ...rest } = action.params;
      const urlSearchParams = new URLSearchParams(rest);
      // eslint-disable-next-line no-unused-expressions
      Array.isArray(states)
        ? states.forEach(s => urlSearchParams.append('params.states', s))
        : urlSearchParams.append('params.states', states);
      urlSearchParams.set('params.limit', MAX_PAGE_SIZE.toString());

      const result = await fetchBookings(urlSearchParams, 0, companyID);
      return fetchAllPreviousBookingsSuccessAction(result);
    }),
  );

const cancelSingleOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_CANCEL_REASONS_SINGLE_ORDER),
    delay(300),
    mergeMap(({ cancelReq }) => {
      const hasCancelReason = !_isEmpty(
        _get(cancelReq, 'req.feedback.comment', ''),
      );
      if (!hasCancelReason) {
        return of({});
      }
      return fetchSubmitCancelReasonsAction(cancelReq).pipe(
        switchMap(() => of({})),
        catchError(() => of({})),
      );
    }),
    switchMap(() => {
      const isPax = isPaxSelector(state$.value);
      const companyID = userCompanyIDSelector(state$.value);
      return isPax
        ? of(paxFetchOngoingDeliveriesIntervalAction())
        : of(fetchOngoingDeliveriesIntervalAction({ companyID }));
    }),
  );

const cancelMultipleOrdersEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_CANCEL_REASONS_FOR_MULTIPLE_ORDERS),
    mergeMap(({ multipleCancelReq }) => {
      const isPax = isPaxSelector(state$.value);
      const obs = from(multipleCancelReq).pipe(
        mergeMap(formValues => {
          const { req } = formValues;
          if (isPax) {
            return fetchPaxCancelSingleDelivery(
              req.deliveryID,
              req.expressServiceID,
            ).pipe(switchMap(() => of(formValues)));
          } else {
            return fetchCancelSingleDelivery(
              req.deliveryID,
              req.isMSDBooking,
            ).pipe(switchMap(() => of(formValues)));
          }
        }),
        delay(300),
        mergeMap(formValues => {
          const hasCancelReason = !_isEmpty(
            _get(formValues, 'req.feedback.comment', ''),
          );
          if (!hasCancelReason) {
            return of({});
          }
          return fetchSubmitCancelReasonsAction(formValues).pipe(
            switchMap(() => of({})),
            catchError(() => of({})),
          );
        }),
      );
      return forkJoin(obs);
    }),
    switchMap(() => {
      const isPax = isPaxSelector(state$.value);
      const companyID = userCompanyIDSelector(state$.value);
      return isPax
        ? of(paxFetchOngoingDeliveriesIntervalAction())
        : of(fetchOngoingDeliveriesIntervalAction({ companyID }));
    }),
  );

const fetchCancelSingleDelivery = (deliveryID, isMSDBooking) =>
  fetchObservableUtil(endpoints.getCancelDeliveryEpt(deliveryID), {
    method: 'DELETE',
    headers: buildHeaders(null, null, null, true),
    credentials: buildCredentials(),
    body: JSON.stringify({
      deliveryID,
      isMSDBooking,
      enableCancellationRules: enableCancellationRules(),
    }),
  });

const fetchPaxCancelSingleDelivery = (deliveryID, expressServiceID) =>
  fetchObservableUtil(
    endpoints.getPaxCancelDeliveryEpt({
      deliveryID,
      expressServiceID,
    }),
    {
      method: 'DELETE',
      headers: buildHeaders(null, null, null, true),
      credentials: buildCredentials(),
    },
  );

const fetchSubmitCancelReasonsAction = formValues =>
  fetchObservableUtil(
    endpoints.getCancelReasonsEpt(),
    {
      method: 'POST',
      headers: buildHeaders(null, null, null, true),
      credentials: buildCredentials(),
      body: JSON.stringify(formValues),
    },
    {
      rethrow: true,
    },
  );

export default combineEpics(
  fetchHistoryConsecutivelyV1Epic,
  fetchOngoingDeliveriesIntervalEpic,
  ongoingOrderCountingEpic,
  recentUpdateSectionEpic,
  fetchAllPreviousBookingsEpic,
  cancelMultipleOrdersEpic,
  cancelSingleOrderEpic,
  fetchPaxOngoingDeliveriesIntervalEpic,
);
